/**
 * The person's watch token, kept off the address. It arrives on the link as
 * `?t=`, and the page's measurement tags report the address they load on, so
 * the token comes off before anything loads and this browser keeps it instead.
 * A reload still finds it; a link copied from the address bar opens the form,
 * where registering again with the same email hands the same seat back.
 */
const KEY = (slug: string) => `fw.watch.${slug}`;

function keep(slug: string, token: string): void {
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      store.setItem(KEY(slug), token);
      return;
    } catch {
      /* the next store, or none: the address still worked this once */
    }
  }
}

function kept(slug: string): string {
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      const got = store.getItem(KEY(slug));
      if (got) return got;
    } catch {
      /* nothing kept in this store */
    }
  }
  return "";
}

/**
 * The token for this talk: the one the link brought, kept and taken off the
 * address, or else the one this browser kept from before.
 */
export function takeWatchToken(slug: string, fromLink: string | null): string {
  if (typeof window === "undefined") return fromLink ?? "";
  if (!fromLink) return kept(slug);
  keep(slug, fromLink);
  const url = new URL(window.location.href);
  if (url.searchParams.has("t")) {
    url.searchParams.delete("t");
    window.history.replaceState(window.history.state, "", url.toString());
  }
  return fromLink;
}

/** Every token this browser kept on this address, so a quick live's link can know who it is. */
export function keptTokens(): string[] {
  const found = new Set<string>();
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        const token = key?.startsWith("fw.watch.") ? store.getItem(key) : null;
        if (token) found.add(token);
      }
    } catch {
      /* nothing kept in this store */
    }
  }
  return [...found];
}

/** The kept token opens no seat any more: forget it, so the form is where they land. */
export function forgetWatchToken(slug: string): void {
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      store.removeItem(KEY(slug));
    } catch {
      /* nothing to forget in this store */
    }
  }
}

/** "Not you?": this browser forgets every place it kept for that token, so the next visit asks who it is. */
export function forgetToken(token: string): void {
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      for (let i = store.length - 1; i >= 0; i--) {
        const key = store.key(i);
        if (key?.startsWith("fw.watch.") && store.getItem(key) === token) store.removeItem(key);
      }
    } catch {
      /* nothing kept in this store */
    }
  }
}
